import { Pipe, PipeTransform } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

@Pipe({
  name: 'statusDate',
  standalone: true // Se usi i moduli tradizionali, rimuovi questo e dichiaralo nel tuo NgModule
})
export class StatusDatePipe implements PipeTransform {

  constructor(private translate: TranslateService) { }

  transform(utcDateString: string | Date | null | undefined): string {
    if (!utcDateString) return '';

    const utcSourceString = utcDateString instanceof Date ? utcDateString.toISOString() : utcDateString;
    // Controlla se la stringa ha già un indicatore di fuso orario, altrimenti forza 'Z' (UTC)
    const utcString = utcSourceString.endsWith('Z') || utcSourceString.includes('+')
      ? utcSourceString
      : `${utcSourceString}Z`;

    // Convertiamo la stringa UTC in un oggetto Date locale
    const inputDate = new Date(utcString);

    if (isNaN(inputDate.getTime())) return '---';

    // 1. Controllo per la data di default "01/01/0001" (indipendentemente dall'ora/fuso orario)
    if (inputDate.getUTCFullYear() <= 2000) {
      return '---';
    }

    const now = new Date();

    // Rileva in automatico la lingua del browser dell'utente (es. 'it-IT', 'en-US')
    const userLocale = navigator.language || 'it-IT';

    // 1. Definiamo i formattatori nativi basati sulle impostazioni locali
    const timeFormatter = new Intl.DateTimeFormat(userLocale, {
      hour: '2-digit',
      minute: '2-digit'
    });

    const dateFormatter = new Intl.DateTimeFormat(userLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    // 2. Azzeriamo le ore per confrontare i giorni solari
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const compareDate = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

    const timeString = timeFormatter.format(inputDate);

    // 3. Logica di restituzione testuale locale
    if (compareDate.getTime() === today.getTime()) {
      // Puoi internazionalizzare anche le parole "oggi" e "ieri" se l'app è multilingua
      return `${this.translate.instant('primeng.today')} ${timeString}`;
    }

    if (compareDate.getTime() === yesterday.getTime()) {
      return this.translate.instant('yesterday');
    }

    // 4. Se antecedente, deleghiamo la formattazione interamente a Intl
    return dateFormatter.format(inputDate);
  }
}

@Pipe({
  name: 'dateOnly',
  standalone: true
})
export class DateOnlyPipe implements PipeTransform {

  constructor(private translate: TranslateService) { }

  transform(utcDateString: string | Date | null | undefined): string {
    if (!utcDateString) return '';

    const utcSourceString = utcDateString instanceof Date ? utcDateString.toISOString() : utcDateString;

    // Controlla se la stringa ha già un indicatore di fuso orario, altrimenti forza 'Z' (UTC)
    const utcString = utcSourceString.endsWith('Z') || utcSourceString.includes('+')
      ? utcSourceString
      : `${utcSourceString}Z`;

    // Convertiamo la stringa UTC in un oggetto Date locale
    const inputDate = new Date(utcString);

    if (isNaN(inputDate.getTime())) return '---';

    // 1. Controllo per la data di default "01/01/0001" (indipendentemente dall'ora/fuso orario)
    if (inputDate.getUTCFullYear() <= 2000) {
      return '---';
    }

    const now = new Date();

    // Rileva in automatico la lingua del browser dell'utente (es. 'it-IT', 'en-US')
    const userLocale = navigator.language || 'it-IT';

    // 1. Definiamo i formattatori nativi basati sulle impostazioni locali
    const dateFormatter = new Intl.DateTimeFormat(userLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });

    // 2. Azzeriamo le ore per confrontare i giorni solari
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const compareDate = new Date(inputDate.getFullYear(), inputDate.getMonth(), inputDate.getDate());

    // 3. Logica di restituzione testuale locale
    if (compareDate.getTime() === today.getTime()) {
      // Puoi internazionalizzare anche le parole "oggi" e "ieri" se l'app è multilingua
      return this.translate.instant('primeng.today');
    }

    if (compareDate.getTime() === yesterday.getTime()) {
      return this.translate.instant('yesterday');
    }

    // 4. Se antecedente, deleghiamo la formattazione interamente a Intl
    return dateFormatter.format(inputDate);
  }
}

@Pipe({
  name: 'duration',
  standalone: true
})
export class DurationPipe implements PipeTransform {

  transform(value: number | string | null | undefined): string {
    if (value == null) return '';

    let totalMinutes: number;

    if (typeof value === 'number') { // Caso 1: numero
      totalMinutes = value;
    } else {                         // Caso 2: stringa
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed)) {
        totalMinutes = parsed;
      } else {
        return '';
      }
    }


    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;

    return `${this.pad(hh)}:${this.pad(mm)}`;
  }

  private pad(n: number): string {
    return n.toString().padStart(2, '0');
  }
}

@Pipe({
  name: 'timeOnly',
  standalone: true
})
export class TimeOnlyPipe implements PipeTransform {

  transform(utcDateString: string | Date | null | undefined): string {
    if (!utcDateString) return '';

    const utcSourceString = utcDateString instanceof Date ? utcDateString.toISOString() : utcDateString;

    // Controlla se la stringa ha già un indicatore di fuso orario, altrimenti forza 'Z' (UTC)
    const utcString = utcSourceString.endsWith('Z') || utcSourceString.includes('+')
      ? utcSourceString
      : `${utcSourceString}Z`;

    // Convertiamo la stringa UTC in un oggetto Date locale
    const inputDate = new Date(utcString);

    if (isNaN(inputDate.getTime())) return '---';

    // 1. Controllo per la data di default "01/01/0001" (indipendentemente dall'ora/fuso orario)
    if (inputDate.getUTCFullYear() <= 2000) {
      return '---';
    }

    const now = new Date();

    // Rileva in automatico la lingua del browser dell'utente (es. 'it-IT', 'en-US')
    const userLocale = navigator.language || 'it-IT';

    // 1. Definiamo i formattatori nativi basati sulle impostazioni locali
    const timeFormatter = new Intl.DateTimeFormat(userLocale, {
      hour: '2-digit',
      minute: '2-digit'
    });

    return timeFormatter.format(inputDate);
  }
}

